import {
    getSyntheticGoalPriorityFromActual,
    MoneyMapRulePriority,
} from '@fruitful/money-map';
import type {
    DeepPartial,
    MoneyMapRuleGroup as MoneyMapRuleGroupType,
    MoneyMapRuleWithAccountDetails,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { BodyMediumRegular, BodySmallRegular, XStack, YStack } from 'smoothie';

import { DragHandle } from './DragHandle';
import { GoalRuleChips } from './GoalRuleChips';
import {
    DRAG_PINK,
    goalTileFocusRingStyle,
    GROUP_GOAL_ROW_MIN_HEIGHT,
} from './goalsWaterfallConstants';
import type { DragHandleProps } from './goalsWaterfallTypes';

interface SingleGoalGroupSectionProps {
    group: DeepPartial<MoneyMapRuleGroupType>;
    rule: DeepPartial<MoneyMapRuleWithAccountDetails>;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    showOutline?: boolean;
    isDragOverlay?: boolean;
    dragHandleProps?: DragHandleProps;
    isSelected?: boolean;
    onPress?: () => void;
}

export const SingleGoalGroupSection = ({
    group,
    rule,
    moneyMap,
    showOutline = false,
    isDragOverlay = false,
    dragHandleProps,
    isSelected = false,
    onPress,
}: SingleGoalGroupSectionProps) => {
    const syntheticPriority = getSyntheticGoalPriorityFromActual(
        group.priority ?? 0,
        moneyMap,
    );

    const isHighlighted = isSelected || showOutline;

    return (
        <XStack
            width="100%"
            minHeight={GROUP_GOAL_ROW_MIN_HEIGHT}
            backgroundColor="white"
            borderRadius={16}
            padding={16}
            gap={12}
            alignItems="center"
            cursor={
                isDragOverlay
                    ? 'grabbing'
                    : onPress != null
                      ? 'pointer'
                      : 'default'
            }
            {...(!isDragOverlay && {
                ...(isHighlighted && {
                    style: goalTileFocusRingStyle(DRAG_PINK),
                }),
            })}
            onPress={!isDragOverlay ? onPress : undefined}
        >
            <MoneyMapRulePriority priority={syntheticPriority} />
            <YStack flex={1} minWidth={0} gap={2}>
                <BodyMediumRegular numberOfLines={1}>
                    {rule.description}
                </BodyMediumRegular>
                {group.description != null && group.description.length > 0 && (
                    <BodySmallRegular color="$gray1000" numberOfLines={2}>
                        {group.description}
                    </BodySmallRegular>
                )}
            </YStack>
            <GoalRuleChips rule={rule} />
            {dragHandleProps != null && <DragHandle {...dragHandleProps} />}
        </XStack>
    );
};
